import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  MessageSquare, FileQuestion, Star, AlertTriangle, Megaphone,
  Send, Mail, Wrench, Clock, Image as ImageIcon, Package, ExternalLink,
} from 'lucide-react';
import { API, authHeaders } from './adminApi';

const emit = (tab) => window.dispatchEvent(new CustomEvent('admin:goto-tab', { detail: tab }));

const StatCard = ({ label, value, icon: Icon, tone = 'default', testid }) => {
  const TONES = {
    default: 'border-slate-800',
    warn: 'border-amber-800/70',
    danger: 'border-red-800/70',
    ok: 'border-emerald-800/70',
  };
  const IC = {
    default: 'text-slate-400',
    warn: 'text-amber-400',
    danger: 'text-red-400',
    ok: 'text-emerald-400',
  };
  return (
    <Card className={`bg-slate-900 ${TONES[tone]}`} data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className={`w-8 h-8 ${IC[tone]}`} />
        <div>
          <div className="text-2xl font-extrabold text-white tabular-nums leading-none">{value ?? '—'}</div>
          <div className="text-[11px] uppercase tracking-widest text-gray-400 mt-1">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickAction = ({ label, icon: Icon, onClick, testid }) => (
  <Button
    data-testid={testid}
    onClick={onClick}
    variant="outline"
    className="border-slate-700 bg-slate-950/40 text-white hover:bg-slate-800 hover:text-white h-auto py-3 justify-start"
  >
    <Icon className="w-4 h-4 mr-2 shrink-0 text-red-500" />
    <span className="text-sm text-left">{label}</span>
  </Button>
);

/**
 * Dashboard top section — at-a-glance status cards + Quick Actions.
 * Renders ABOVE the existing inbox tables/analytics so nothing about
 * the existing dashboard structure is lost.
 */
const DashboardOverview = ({ activeAnnouncement }) => {
  const [counts, setCounts] = useState({
    new_contacts: null,
    new_quotes: null,
    awaiting_reply: null,
    review_ready: null,
  });
  const [bannerActive, setBannerActive] = useState(Boolean(activeAnnouncement));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [contacts, quotes, banner] = await Promise.all([
          axios.get(`${API}/api/contact`, { headers: authHeaders() }),
          axios.get(`${API}/api/quote`, { headers: authHeaders() }),
          axios.get(`${API}/api/banner`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const contactList = contacts.data || [];
        const quoteList = quotes.data || [];
        const newContacts = contactList.filter((s) => s.status === 'new').length;
        // QuoteRequest defaults to 'pending' — count anything not replied/completed as new-ish
        const newQuotes = quoteList.filter((q) => q.status === 'pending' || q.status === 'new').length;
        const awaiting = [
          ...contactList.filter((s) => s.status === 'read'),
          ...quoteList.filter((q) => q.status === 'reviewed'),
        ].length;
        setCounts({
          new_contacts: newContacts,
          new_quotes: newQuotes,
          awaiting_reply: awaiting,
          review_ready: 0,  // Stage 3 will populate this from customer completion state
        });
        if (banner?.data?.enabled) setBannerActive(true);
      } catch {
        /* keep loading nulls */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section data-testid="dashboard-overview" className="space-y-6">
      {/* Status cards */}
      <div>
        <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Attention</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="New contact requests" value={counts.new_contacts} icon={MessageSquare} tone={counts.new_contacts ? 'warn' : 'default'} testid="dash-new-contacts" />
          <StatCard label="New quote requests" value={counts.new_quotes} icon={FileQuestion} tone={counts.new_quotes ? 'warn' : 'default'} testid="dash-new-quotes" />
          <StatCard label="Awaiting reply" value={counts.awaiting_reply} icon={Clock} tone={counts.awaiting_reply ? 'warn' : 'default'} testid="dash-awaiting-reply" />
          <StatCard label="Review requests ready" value={counts.review_ready} icon={Star} tone={counts.review_ready ? 'warn' : 'default'} testid="dash-review-ready" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <StatCard label="Failed emails" value={0} icon={AlertTriangle} tone="default" testid="dash-failed-emails" />
          <StatCard label="Draft website changes" value={0} icon={Wrench} tone="default" testid="dash-draft-website" />
          <StatCard
            label="Active announcement"
            value={bannerActive ? 'On' : 'Off'}
            icon={Megaphone}
            tone={bannerActive ? 'ok' : 'default'}
            testid="dash-active-announcement"
          />
          <StatCard label="Draft catalog products" value={0} icon={Package} tone="default" testid="dash-draft-catalog" />
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Quick actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          <QuickAction label="Reply to a message" icon={MessageSquare} onClick={() => emit('inbox')} testid="qa-reply" />
          <QuickAction label="Send customer email" icon={Mail} onClick={() => emit('emails')} testid="qa-email" />
          <QuickAction label="Send Google review request" icon={Star} onClick={() => emit('emails')} testid="qa-review-request" />
          <QuickAction label="Edit website" icon={Wrench} onClick={() => emit('website')} testid="qa-edit-website" />
          <QuickAction label="Change business hours" icon={Clock} onClick={() => emit('website')} testid="qa-hours" />
          <QuickAction label="Add an announcement" icon={Megaphone} onClick={() => emit('announcements')} testid="qa-announcement" />
          <QuickAction label="Upload gallery photos" icon={ImageIcon} onClick={() => emit('gallery')} testid="qa-gallery" />
          <QuickAction label="Add catalog product" icon={Package} onClick={() => emit('catalog')} testid="qa-catalog" />
          <QuickAction label="View live website" icon={ExternalLink} onClick={() => window.open('/', '_blank')} testid="qa-view-site" />
        </div>
      </div>
    </section>
  );
};

export default DashboardOverview;
